import clsx from "clsx";
import React, { useMemo } from "react";
import Markdown from "react-markdown";
import rehypeKatex from "rehype-katex";
import rehypeSanitize, { defaultSchema } from "rehype-sanitize";
import remarkGfm from "remark-gfm";
import remarkMath from "remark-math";
import { useArtifactDownload } from "@/hooks/useArtifactDownload";
import type { CitationSource, SourceRef } from "@/types/ask";
import { normalizeCitationMarkup } from "@/utils/chat/citations";
import rehypeCitationRefs from "@/utils/chat/rehypeCitationRefs";
import { normalizeMathDelimiters } from "@/utils/common/math";
import { createRehypeSanitizeSchema } from "@/utils/common/security";
import CitationGroupPill from "./CitationGroupPill";
import CitationSpan from "./CitationSpan";
import LinkRenderer from "./LinkRenderer";
import { markdownTableComponents } from "./MarkdownTableWithCopy";
import remarkGridPlugin from "./remark-grid-plugin";

const sanitizeSchema = createRehypeSanitizeSchema(defaultSchema);

const MarkdownSegment: React.FC<{
  text: string;
  isV1: boolean;
  sendAction?: (title: string, action: string | null) => Promise<void>;
  citations?: CitationSource[];
  preserveEmoji?: boolean;
}> = ({ text, isV1, sendAction, citations, preserveEmoji = false }) => {
  const triggerDownload = useArtifactDownload();
  const normalizedText = preserveEmoji
    ? text
    : text.replaceAll(/[\p{Emoji_Presentation}\p{Extended_Pictographic}]|\d\uFE0F?\u20E3/gu, "");

  // Build sources array for v1 citation pills, sorted by citationNumber
  // so sources[N-1] reliably maps to citation number N
  const sources: SourceRef[] | undefined = useMemo(() => {
    if (!isV1 || !citations || citations.length === 0) return undefined;
    const sorted = [...citations].sort((a, b) => a.citationNumber - b.citationNumber);
    return sorted.map((c) => ({
      url: c.url,
      fileName: c.fileName ?? c.title,
      description: undefined,
      id: c.documentId,
      pages: c.pages,
      sourceType: c.sourceType,
    }));
  }, [isV1, citations]);

  // V1: normalizeCitationMarkup for grouped citation pills
  // Default: rehypeCitationRefs plugin for inline badges
  const processedText = useMemo(() => {
    if (isV1) {
      let processed = normalizedText;
      if (sources && sources.length > 0) {
        const maxCitationNum = citations!.reduce((max, c) => Math.max(max, c.citationNumber), 0);
        processed = normalizeCitationMarkup(processed, maxCitationNum);
      } else {
        // No citations for this message — strip stale [^N] markers from rendered text
        processed = processed.replaceAll(/ ?\[\^?\d{1,3}\](?!\()/g, "");
      }
      return normalizeMathDelimiters(processed);
    }
    return normalizeMathDelimiters(
      normalizedText.replaceAll(/\[\^(\d+)\]/g, String.raw`\[^$1\]`)
    );
  }, [isV1, normalizedText, sources]);

  const SpanComponent = useMemo(
    () =>
      function CitationSpanWrapper(props: any) {
        return <CitationSpan {...props} citations={citations} />;
      },
    [citations]
  );

  const V1LinkComponent = useMemo(
    () =>
      function V1Link(props: any) {
        const href = props.href || "";
        if (href.startsWith("#cite-group-")) {
          return <CitationGroupPill nums={href.replace("#cite-group-", "")} sources={sources} />;
        }
        return LinkRenderer(props, sendAction, triggerDownload);
      },
    [sources, sendAction, triggerDownload]
  );

  const DefaultLinkComponent = useMemo(
    () =>
      function DefaultLink(props: any) {
        return LinkRenderer(props, sendAction, triggerDownload);
      },
    [sendAction, triggerDownload]
  );

  if (isV1) {
    return (
      <Markdown
        className="mrkdwn-v1"
        components={{
          a: V1LinkComponent,
          img: () => null,
          ...markdownTableComponents,
        }}
        remarkPlugins={[remarkMath, remarkGfm, remarkGridPlugin]}
        rehypePlugins={[rehypeKatex, [rehypeSanitize, sanitizeSchema]]}
      >
        {processedText}
      </Markdown>
    );
  }

  return (
    <Markdown
      className={clsx(
        "[&_a]:underline",
        "[&_a]:text-primary",
        "mrkdwn",
        "[&_table]:border-collapse",
        "[&_table]:border",
        "[&_table]:border-300",
        "[&_table]:my-4",
        "[&_table]:w-max",
        "[&_th]:border",
        "[&_th]:border-300",
        "[&_th]:bg-background",
        "[&_th]:px-4",
        "[&_th]:py-2",
        "[&_th]:text-left",
        "[&_th]:font-semibold",
        "[&_td]:border",
        "[&_td]:border-300",
        "[&_td]:px-4",
        "[&_td]:py-2",
        "[&_h3]:text-base",
        "[&_h3]:font-medium"
      )}
      components={{
        a: DefaultLinkComponent,
        img: () => null,
        span: SpanComponent,
        ...markdownTableComponents,
      }}
      remarkPlugins={[remarkMath, remarkGfm, remarkGridPlugin]}
      rehypePlugins={[
        rehypeCitationRefs,
        rehypeKatex,
        [rehypeSanitize, sanitizeSchema],
      ]}
    >
      {processedText}
    </Markdown>
  );
};

export default MarkdownSegment;
